import {Col, Container, Modal, Row} from "react-bootstrap";
import {AppContext} from "../../lib/AppContext";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {handleDefaultValues} from "../common/Utils";
import {useForm} from 'react-hook-form';
import MDTypography from "../../components/MDTypography";
import {Divider, InputAdornment, NativeSelect} from "@mui/material";
import {getAccountTypeEventTypes, getInventoryWaivers} from "../common/api/InventoryApi";
import MDButton from "../../components/MDButton";
import MDInput from "../../components/MDInput";
import Switch from "@mui/material/Switch";
import {CompanyContext} from "../../lib/CompanyContext";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import {useAccess} from "../../lib/AccessProvider";
import {CONSTANTS} from "../common/constants";

export default function ManageAccountTypeModal(props) {

  const rotate = {
    'transform': 'rotate(270deg)'
  };

  const recurTypes = [CONSTANTS.RECURRENCE_TYPES.RECURRENCES_WEEKLY, CONSTANTS.RECURRENCE_TYPES.RECURRENCES_MONTHLY, CONSTANTS.RECURRENCE_TYPES.RECURRENCES_ANNUALLY, CONSTANTS.RECURRENCE_TYPES.TIME_BASED_RECURRING]

  const [recurrenceType, setRecurrenceType] = useState();

  const [context, setContext] = useContext(AppContext);
  const [companyContext, setCompanyContext] = useContext(CompanyContext);
  const [accountType, setAccountType] = useState(props.accountType || {});

  const [recurring, setRecurring] = useState(props?.accountType?.recurring);
  const [timeBased, setTimeBased] = useState(props?.accountType?.timeBased);

  const [unlimitedVisits, setUnlimitedVisits] = useState(props?.accountType?.unlimitedVisits);
  const [familyPass, setFamilyPass] = useState(props?.accountType?.familyPass);

  const [decrementVisits, setDecrementVisits] = useState(props?.accountType?.decrementVisits);
  const [siblingDiscount, setSiblingDiscount] = useState(props?.accountType?.siblingDiscount);
  const [siblingOptionsEnabled, setSiblingOptionsEnabled] = useState(props?.accountType?.siblingOptionsEnabled);

  const [eventTypesFilter, setEventTypesFilter] = useState(props.accountType?.eventTypes || []);
  const [formInvalid, setFormInvalid] = useState(false);

  const [isNew, setIsNew] = useState(!!!props.accountType?.guid);
  const [expandMemberOptions, setExpandMemberOptions] = useState(true);
  const [expandLinkedEventTypes, setExpandLinkedEventTypes] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);

  const [companyLinkEventTypes, setCompanyLinkEventTypes] = useState(null);

  const [waivers, setWaivers] = useState();
  const [orgAccess, setOrgAccess] = useState(null);

  const {getAccess} = useAccess();
  useEffect(() => {
    setOrgAccess(getAccess());
  }, []);

  useEffect(() => {
    if (props?.accountType) {
      setRecurrenceType(props?.accountType?.recurrencePattern);
    } else {
      setUnlimitedVisits(false);
      setDecrementVisits(true);
    }
  }, [props?.accountType]);

  useEffect(() => {
    let _accountType = {...accountType};
    _accountType.recurrencePattern = recurrenceType;
    setAccountType(_accountType);
  }, [recurrenceType]);

  const groupedByCompany = useMemo(() => {
    const map = new Map();
    [...eventTypes]
      .sort((a, b) => {
        const aP = a.companyId === companyContext?.company?.companyId;
        const bP = b.companyId === companyContext?.company?.companyId;
        return aP === bP ? 0 : aP ? -1 : 1;
      })
      .forEach(({companyId, companyName, ...eventType}) => {
        if (parseInt(companyId, 10) === parseInt(context?.sessionUser?.company?.companyId, 10) || orgAccess?.orgOwner) {
          if (!map.has(companyId)) {
            map.set(companyId, {
              companyId,
              companyName,
              eventTypes: [],
            });
          }
          map.get(companyId).eventTypes.push(eventType);
        }
      });

    return [...map.values()];
  }, [eventTypes]);


  const loadInventoryEventTypesFn = async () => {
    getAccountTypeEventTypes().then((result) => {
      if (result.error) {
        setContext({...context, error: {message: result.message}});
      } else {
        setEventTypes(result);
      }
    });
  }

  useEffect(() => {
    loadInventoryEventTypesFn();
  }, [])

  useEffect(() => {
    getInventoryWaivers().then(result => {
      if (result.error) {
        setContext({...context, error: {message: result.message}});
      } else {
        setWaivers(result);
      }
    });
  }, [])

  useEffect(() => {
    setAccountType(props?.accountType);
  }, [props?.accountType]);

  const handleFormChange = (e) => {
    setValue(e.target.name, e.target.value);
    let _obj = {...accountType};
    _obj[e.target.name] = e.target.value;
    setAccountType({..._obj});
  };

  const toggleRecurring = (e) => {
    setValue(e.target.name, e.target.checked);
    let _obj = {...accountType};
    _obj[e.target.name] = e.target.checked;
    setAccountType({..._obj});
    if (e.target.checked) {
      setValue('recurrencePattern', CONSTANTS.RECURRENCE_TYPES.RECURRENCES_WEEKLY);
    } else {
      setValue('recurrencePattern', null);
    }
  }

  const toggleUnlimited = (e) => {
    setValue(e.target.name, e.target.checked);
    let _obj = {...accountType};
    _obj[e.target.name] = e.target.checked;
    setAccountType({..._obj});
  }

  const {register, handleSubmit, setValue, control} = useForm({
    values: handleDefaultValues(accountType)
  });

  const handleClose = () => {
    props.handleClose();
  };

  const handleSave = (data) => {
    let d = {...data};
    d.eventTypes = eventTypesFilter;
    d.recurring = recurring;
    d.unlimitedVisits = unlimitedVisits;
    d.decrementVisits = decrementVisits;
    d.familyPass = familyPass;
    d.timeBased = timeBased;
    d.siblingOptionsEnabled = siblingOptionsEnabled;
    props.handleSave(d);
  };

  const currentCompany = (c) => {
    return c.companyId === companyContext?.company?.companyId;
  }

  const handleLinkedEventTypeChange = (e) => {
    let _obj = [...eventTypesFilter];
    if (e.target.checked) {
      _obj = [...eventTypesFilter, parseInt(e.target.value, 10)];
    } else {
      _obj.splice(eventTypesFilter.indexOf(parseInt(e.target.value, 10)), 1);
    }
    setEventTypesFilter(_obj);
  }

  const updateRecurrence = (value) => {
    setRecurrenceType(value);
    if (value === 'NONE') {
      setTimeBased(false);
      setRecurring(false);
    } else if (value === 'TIME_BASED') {
      setRecurring(false);
      setTimeBased(true);
    } else {
      setTimeBased(false);
      setRecurring(true);
    }
  }

  const handleUnlimitedVisitsToggle = (e) => {
    setUnlimitedVisits(e.target.checked);
    setDecrementVisits(!e.target.checked);
  }

  const handleFamilyPassToggle = (e) => {
    setFamilyPass(e.target.checked);
    if (e.target.checked) {
      setSiblingOptionsEnabled(false);
      setSiblingDiscount(0);
    }
  }

  const handleSiblingOptionsToggle = (e) => {
    setSiblingOptionsEnabled(e.target.checked);
  }

  return (
    <Modal onHide={handleClose} size={'lg'} show={props.show}>
      <form onSubmit={handleSubmit(handleSave)} id={'accountTypeForm'}>
        <Modal.Header closeButton>
          <Modal.Title>{isNew ? 'Create New' : 'Manage'} Account Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col className={'col col-main'} xs={12}>
              <Container>
                <Row>
                  <Col className={'col'} sm={12} xs={12}>
                    <div className="form-group">
                      <MDInput type="text" required
                               {...register('name')}
                               autoComplete="off"
                               className="form-control" id="name"
                               InputLabelProps={{shrink: true}}
                               label="Account Type Name"
                               placeholder="Enter Account Type Name"/>
                    </div>
                  </Col>
                </Row>

                <Row className={'mt-3'}>
                  <Col className={'col'} sm={12} xs={12}>
                    <div className="form-group">
                      <MDInput type="text"
                               {...register('desc')}
                               autoComplete="off"
                               maxLength={255}
                               InputLabelProps={{shrink: true}}
                               className="form-control" id="desc"
                               label="Account Type Description"
                               placeholder="Enter Account Type Description"/>
                    </div>
                  </Col>
                </Row>

                <Row className={'mt-2'}>
                  <MDTypography variant={'h6'}>Member Options
                    <Divider/>
                  </MDTypography>
                  <Row>
                    <div>
                      <Row>

                        <Col className={'col'} sm={6} xs={12}>
                          <div className="form-group">
                            <label htmlFor="accountType">
                              Recurring Memberships
                            </label>
                            <div className={'form-group'}>
                              <NativeSelect
                                variant="outlined"
                                value={accountType?.recurrencePattern}
                                className={'form-control'}
                                {...register('accountType.recurrencePattern')}
                                sx={{
                                  width: '100%'
                                }}
                                onChange={
                                  (e) => {
                                    updateRecurrence(e.target.value)
                                  }
                                }>
                                <option
                                  defaultChecked={!accountType?.recurrencePattern}
                                  value={'NONE'}>Does not Auto Renew
                                </option>

                                <option
                                  defaultChecked={accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.TIME_BASED}
                                  value={CONSTANTS.RECURRENCE_TYPES.TIME_BASED}>Time Based Membership
                                </option>

                                <option
                                  defaultChecked={accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.RECURRENCES_MONTHLY}
                                  value={CONSTANTS.RECURRENCE_TYPES.RECURRENCES_MONTHLY}>Auto Renews Monthly
                                </option>
                                <option
                                  defaultChecked={accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.RECURRENCES_ANNUALLY}
                                  value={CONSTANTS.RECURRENCE_TYPES.RECURRENCES_ANNUALLY}>Auto Renews Annually
                                </option>

                                <option
                                  defaultChecked={accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.TIME_BASED_RECURRING}
                                  value={CONSTANTS.RECURRENCE_TYPES.TIME_BASED_RECURRING}>Time Based Recurring
                                </option>

                              </NativeSelect>
                            </div>
                          </div>
                        </Col>

                      </Row>

                      {(accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.TIME_BASED || accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.TIME_BASED_RECURRING) && <>
                        <Row className={'mt-2'}>
                          <Col className={'col'} sm={6} xs={12}>
                            <div className="form-group">
                              <label className={'cursor-pointer mb-2'}
                                     htmlFor={'unlimitedVisitsToggle'}>Membership Period
                              </label>
                              <MDInput type="number"
                                       required={accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.TIME_BASED || accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.TIME_BASED_RECURRING}
                                       min={1}
                                       {...register('membershipTimeDays', {
                                         valueAsNumber: true
                                       })}
                                       InputLabelProps={{shrink: true}}
                                       label={'Membership ' + (accountType?.recurrencePattern === CONSTANTS.RECURRENCE_TYPES.TIME_BASED ? 'Expires' : 'Renews') + ' Days'}
                                       placeholder="ie. 30, 60, 90"
                                       className="form-control"
                                       autoComplete="off"
                                       id="membershipTimeDays"/>


                            </div>
                          </Col>
                          <p className={'py-2 fw-light'}>
                            Time based memberships are prepaid memberships that expire after the set period. For
                            example,
                            a weekend, holiday period, or multiple month memberships.</p>
                        </Row>
                      </>
                      }

                      <Row>
                        <Col className={'col'} sm={6} xs={12}>
                          <div className="form-group">
                            <label htmlFor="inventoryWaiver">Account Waiver</label>
                            {waivers && waivers.length > 0 && <NativeSelect
                              variant="outlined"
                              defaultValue={-1}
                              id={'inventoryWaiver'}
                              name={'inventoryWaiver'}
                              className={'form-control'}
                              {...register('waiverGk', {
                                valueAsNumber: true,
                              })}>
                              <option value={-1}>Select Waiver</option>
                              {
                                waivers.map((c) =>
                                  <option value={c.id} key={c.id}>
                                    {c.waiverName}
                                  </option>)
                              }
                            </NativeSelect>}
                          </div>
                        </Col>
                        <Col className={'col'} xs={12}>
                          <p className={'py-2 fw-light'}>
                            Account Waivers outline terms for specific memberships like recurring or special agreements
                            when linked to a Membership Type.
                          </p>
                        </Col>
                      </Row>

                      <Row className={'mt-2'}>
                        <Col className={'col'} sm={6} xs={12}>
                          <div className="form-group">
                            <MDInput type="text"
                                     InputLabelProps={{shrink: true}}
                                     label={"Purchase Notification"}
                                     {...register('notificationEmail')}
                                     name={'notificationEmail'}
                                     autoComplete="off"
                                     maxLength={155}
                                     className="form-control" id="notificationEmail"
                                     placeholder="Purchase Notification Email"/>
                          </div>
                        </Col>
                      </Row>

                      <Row className={'mt-2'}>
                        <Col className={'col'} sm={6} xs={12}>
                          <div className="form-group">
                            <label className={'cursor-pointer'}
                                   htmlFor={'unlimitedVisitsToggle'}>Unlimited Visits
                            </label>
                            <Switch
                              name={'unlimitedVisitsToggle'}
                              id={'unlimitedVisitsToggle'}
                              style={{marginRight: '5px'}}
                              value={'unlimitedVisitsToggle'}
                              defaultChecked={accountType?.unlimitedVisits}
                              onChange={(e) => {
                                handleUnlimitedVisitsToggle(e);
                              }}
                            />

                          </div>
                        </Col>
                      </Row>
                      <Row className={'mt-2'}>
                        <Col className={'col'} sm={6} xs={12}>

                          <div className="form-group">
                            <MDInput type="number"
                                     required={decrementVisits}

                                     min={decrementVisits ? 1 : 0}
                                     disabled={unlimitedVisits}
                                     {...register('numberVisits', {
                                       valueAsNumber: true
                                     })}
                                     InputLabelProps={{shrink: true}}
                                     label="Number of Visits for membership"
                                     placeholder="Enter Number of visits for this account type"
                                     className="form-control"
                                     autoComplete="off"
                                     id="numberVisits"/>
                          </div>

                        </Col>
                      </Row>

                      <Row className={'mt-2'}>
                        <Col className={'col'} sm={12} xs={12}>
                          <div className="form-group">
                            <label className={'cursor-pointer'}
                                   htmlFor={'familyPassToggle'}>Family Pass
                            </label>
                            <Switch
                              name={'familyPassToggle'}
                              id={'familyPassToggle'}
                              style={{marginRight: '5px'}}
                              value={'familyPassToggle'}
                              defaultChecked={accountType?.familyPass}
                              onChange={(e) => {
                                handleFamilyPassToggle(e);
                              }}
                            />

                            <p className={'py-2 fw-light'}>Family pass allows all family members to use the same
                              membership.
                              If a separate membership is required for siblings, disable this option and optionally add
                              a Sibling discount.</p>
                          </div>

                        </Col>

                      </Row>

                      <Row className={'mt-2'}>
                        <Col className={'col'} sm={6} xs={12}>
                          <div className="form-group">
                            <label className={'cursor-pointer'}
                                   htmlFor={'siblingOptionsToggle'}>Sibling Discount
                            </label>
                            <Switch
                              name={'siblingOptionsToggle'}
                              id={'siblingOptionsToggle'}
                              style={{marginRight: '5px'}}
                              value={'siblingOptionsToggle'}
                              disabled={familyPass}
                              checked={siblingOptionsEnabled}
                              onChange={(e) => {
                                handleSiblingOptionsToggle(e);
                              }}
                            />

                          </div>

                          <div className="form-group">
                            <MDInput type="number"
                                     required={siblingOptionsEnabled}
                                     min={siblingOptionsEnabled ? 1 : 0}
                                     disabled={!siblingOptionsEnabled || familyPass}
                                     {...register('siblingDiscount', {
                                       valueAsNumber: true
                                     })}
                                     InputProps={{
                                       startAdornment: <InputAdornment position="start">
                                         <span className={'fa fa-dollar-sign'}></span>
                                       </InputAdornment>
                                     }}
                                     InputLabelProps={{shrink: true}}
                                     label="Sibling Discount"
                                     placeholder="Enter Discount for siblings using this account type"
                                     className="form-control"
                                     autoComplete="off"
                                     id="siblingDiscount"/>
                          </div>
                        </Col>
                      </Row>

                    </div>
                  </Row>
                </Row>

                <Row className={'mt-2'}>
                  <MDTypography variant={'h6'}>Linked Event Types
                    <Divider/>
                  </MDTypography>
                  <Row>
                    <div>
                      <Row>

                        <Col className={'col'} sm={12} xs={12}>
                          {groupedByCompany.map(company => (
                            <Grid container key={company.companyId}>
                              {orgAccess?.orgOwner && <h6 className={'mt-2'} style={{
                                width: '100%',
                                clear: 'both'
                              }}>{company.companyName}</h6>}
                              {company.eventTypes
                                .map((eventType, index) => (
                                  <Grid item xs={12} sm={6} key={eventType.id}>
                                    <label htmlFor={'linkedEventTypes-' + eventType.id}>
                                      <Checkbox
                                        name={'linkedEventTypes-' + eventType.id}
                                        id={'linkedEventTypes-' + eventType.id}
                                        checked={eventTypesFilter.indexOf(eventType.id) > -1}
                                        {...register('linkedEventTypes[]', {})}
                                        value={parseInt(eventType.id, 10)} key={eventType.id}
                                        onChange={(e) => {
                                          handleLinkedEventTypeChange(e);
                                        }}/>
                                      &nbsp;{eventType.name}
                                    </label>
                                  </Grid>
                                ))}
                            </Grid>
                          ))}

                        </Col>
                      </Row>
                    </div>
                  </Row>
                </Row>

              </Container>
            </Col>
          </Row>

        </Modal.Body>
        <Modal.Footer>
          <MDButton type="submit" color="info" disabled={formInvalid || props.saving}>
            {props.saving ? 'Saving...' : 'Save'}
          </MDButton>
        </Modal.Footer>
      </form>
    </Modal>
  );
}